import React, { useState, useRef } from 'react';
import { Label, FormGroup, Input } from 'reactstrap';
import { Query } from 'react-apollo';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { OBmeterPointStatusQuery } from '../../queries/Queries';
import { Loading } from '../Loading/Loading';

const getMostRecentSupplyEntry = (meterPoint) => {
  if (!meterPoint || !meterPoint.mDMEstateSupplyList || meterPoint.mDMEstateSupplyList.length === 0) {
    return null;
  }

  // Sort by dateOfAction descending to get the most recent entry
  const sortedSupplyHistory = [...meterPoint.mDMEstateSupplyList].sort((a, b) => {
    const dateA = a.dateOfAction ? new Date(a.dateOfAction) : new Date(0);
    const dateB = b.dateOfAction ? new Date(b.dateOfAction) : new Date(0);
    return dateB - dateA;
  });

  return sortedSupplyHistory[0] || null;
};

// Helper function to calculate energisation status from supply history
const getEnergisationStatus = (meterPoint) => {
  const mostRecentEntry = getMostRecentSupplyEntry(meterPoint);
  if (!mostRecentEntry) {
    return '';
  }

  // If isStopsupply is false (Supply = Y) → return "E" (Energised)
  // If isStopsupply is true (Supply = N) → return "D" (De-energised)
  return mostRecentEntry.isStopsupply ? 'D' : 'E';
};

const getMostRecentSupplyDate = (meterPoint) => {
  const mostRecentEntry = getMostRecentSupplyEntry(meterPoint);
  return mostRecentEntry && mostRecentEntry.dateOfAction ? mostRecentEntry.dateOfAction : null;
};

const MeterPointSelector = ({
  value,
  onChange,
  formContext,
  placeholder,
  disabled,
  statusFilter = 'L', // Default to 'Live', but allow customization
  excludeStatusFilter = null, // When set, excludes meter points with this status (e.g. 'L' to exclude Live)
  includeAllStatuses = false, // Option to include all statuses
  includeMeterDropdown = false, // Option to include meter selection dropdown
  onMeterChange, // Callback for meter selection
  meterValue, // Selected meter value
  includeMarketSector = false, // When true, onChange receives { name, marketSector } instead of just name
  includeEnergisationStatus = false, // When true, onChange receives { name, energisationStatus } instead of just name
  includeSupplyDate = false, // When true, onChange receives { name, supplyDate } instead of just name
  onMeterPointSelect, // Callback with full meter point object on selection
}) => {
  const [query, setQuery] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false); // New state for selection loading
  const typeaheadRef = useRef(null);

  // Build the where clause with both status filter and name search
  const whereClause = (() => {
    if (includeAllStatuses) {
      // Include all meter points regardless of status
      return query ? `name like '%${query}%'` : '1=1';
    } else if (excludeStatusFilter) {
      // Exclude meter points with specific status
      return query
        ? `mDMMeterPointStatus != '${excludeStatusFilter}' and name like '%${query}%'`
        : `mDMMeterPointStatus != '${excludeStatusFilter}'`;
    } else {
      // Filter by specific status
      return query
        ? `mDMMeterPointStatus = '${statusFilter}' and name like '%${query}%'`
        : `mDMMeterPointStatus = '${statusFilter}'`;
    }
  })();

  const handleSearch = (searchQuery) => {
    setQuery(searchQuery);
  };

  const handleChange = (selected) => {
    if (selected && selected.length > 0 && selected[0]) {
      setIsSelecting(true); // Start selection loading
      setSelectedOption(selected[0]);
      setSelectedMeter(null); // Reset meter selection when MPAN changes
      if (formContext && typeof formContext.handleChanges === 'function') {
        formContext.handleChanges('selectedMeterPoint', selected[0]);
        formContext.handleChanges('selectedMeter', null); // Reset meter in form context
      }
      if (onChange) {
        // Send the MPAN name (core number) to the form, optionally with additional data
        if (includeMarketSector || includeEnergisationStatus || includeSupplyDate) {
          const payload = { name: selected[0].name };

          if (includeMarketSector) {
            payload.marketSector = selected[0].marketSector;
          }

          if (includeEnergisationStatus) {
            payload.energisationStatus = getEnergisationStatus(selected[0]);
          }

          if (includeSupplyDate) {
            payload.supplyDate = getMostRecentSupplyDate(selected[0]);
          }

          onChange(payload);
        } else {
          onChange(selected[0].name);
        }
      }
      if (onMeterPointSelect) {
        onMeterPointSelect(selected[0]);
      }
      if (onMeterChange) {
        onMeterChange(null); // Reset meter selection
      }
    } else {
      setSelectedOption(null);
      setSelectedMeter(null);
      if (formContext && typeof formContext.handleChanges === 'function') {
        formContext.handleChanges('selectedMeterPoint', null);
        formContext.handleChanges('selectedMeter', null);
      }
      if (onChange) {
        onChange(null); // Clear selection
      }
      if (onMeterPointSelect) {
        onMeterPointSelect(null);
      }
      if (onMeterChange) {
        onMeterChange(null);
      }
    }

    // End selection loading after a brief delay to allow re-render
    setTimeout(() => setIsSelecting(false), 100);
  };

  const handleMeterChange = (e) => {
    const meterId = e.target.value;
    const meterRegisterList = selectedOption && selectedOption.mDMMeterRegisterList;
    const meterRegister = meterRegisterList && meterRegisterList.find((reg) => reg.meter && reg.meter.id === meterId);
    const meter = meterRegister && meterRegister.meter;

    setSelectedMeter(meter);

    if (formContext && typeof formContext.handleChanges === 'function') {
      formContext.handleChanges('selectedMeter', meter);
    }

    if (onMeterChange) {
      onMeterChange(meter);
    }
  };

  const getSelectedValue = (result) => {
    if (selectedOption && selectedOption.name) {
      return [selectedOption];
    }

    if (value && result.data && result.data.OB_meterPoint) {
      // Handle both object and string value modes.
      const valueName =
        (includeMarketSector || includeEnergisationStatus || includeSupplyDate) && value && typeof value === 'object'
          ? value.name
          : value;
      // Look for option by name (MPAN core) since that's what we're storing in the form
      const foundOption = result.data.OB_meterPoint.find((option) => option.name === valueName);
      if (foundOption && foundOption.name) {
        // Initialize selectedOption if we found it from props
        if (!selectedOption) {
          setSelectedOption(foundOption);

          // Initialize selected meter if meterValue is provided
          if (meterValue && foundOption.mDMMeterRegisterList) {
            const foundMeterRegister = foundOption.mDMMeterRegisterList.find(
              (reg) => reg.meter && reg.meter.id === meterValue,
            );
            const foundMeter = foundMeterRegister && foundMeterRegister.meter;
            if (foundMeter && !selectedMeter) {
              setSelectedMeter(foundMeter);
            }
          }
        }
        return [foundOption];
      }
    }

    return [];
  };

  const getAvailableMeters = () => {
    if (!selectedOption || !selectedOption.mDMMeterRegisterList) {
      return [];
    }
    return selectedOption.mDMMeterRegisterList
      .map(({ meter }) => meter)
      .filter((meter, index, self) => self.findIndex((m) => m.id === meter.id) === index); // Unique meters
  };

  return (
    <Query
      query={OBmeterPointStatusQuery}
      variables={{
        where: whereClause,
      }}
      skip={!query && !value} // Skip query if no search term and no selected value
    >
      {(result) => (
        <div>
          <AsyncTypeahead
            ref={typeaheadRef}
            placeholder={placeholder || 'Search meter points...'}
            disabled={disabled || !!result.error || result.loading || isSelecting}
            onSearch={handleSearch}
            cssclassName='md-12'
            options={
              result.data && result.data.OB_meterPoint
                ? result.data.OB_meterPoint.filter((option) => option && option.name)
                : []
            }
            filterBy={() => true} // Let the server handle filtering
            labelKey='name'
            selectHintOnEnter={true}
            onChange={handleChange}
            selected={getSelectedValue(result)}
            clearButton={true}
            renderMenuItemChildren={(option, props, index) => (
              <div key={index}>
                <div>
                  <strong>{option.name}</strong>
                </div>
                <div>
                  <small className='text-muted'>
                    Status: {option.mDMMeterPointStatus || 'N/A'} | ID: {option.id}
                  </small>
                </div>
              </div>
            )}
          >
            {result.loading || isSelecting ? <Loading /> : null}
          </AsyncTypeahead>
          {result.error && (
            <div className='alert alert-danger mt-2' role='alert'>
              Error loading meter points. Please try again.
            </div>
          )}
          {includeMeterDropdown && selectedOption && getAvailableMeters().length > 0 && (
            <FormGroup className='mt-3'>
              <Label for='meter-select'>Meter</Label>
              <Input
                type='select'
                id='meter-select'
                value={(selectedMeter && selectedMeter.id) || meterValue || ''}
                onChange={handleMeterChange}
                disabled={disabled || result.loading}
              >
                <option value=''>Select Meter</option>
                {getAvailableMeters().map((meter) => (
                  <option key={meter.id} value={meter.id}>
                    {meter.serialNumber} (ID: {meter.id})
                  </option>
                ))}
              </Input>
            </FormGroup>
          )}
        </div>
      )}
    </Query>
  );
};

export default MeterPointSelector;
